import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Send, Search, Video, Gift as GiftIcon } from "lucide-react";
import { toast } from "sonner";
import api, { errText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCall } from "@/context/CallContext";
import { CoinIcon } from "@/components/Icons";
import Avatar from "@/components/Avatar";
import BottomNav from "@/components/BottomNav";

function timeAgo(iso) {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

function GiftSheet({ userId, onSent, onClose }) {
  const [gifts, setGifts] = useState(null);
  const [sending, setSending] = useState(false);
  const { setUser } = useAuth();

  useEffect(() => {
    api.get("/gifts").then(({ data }) => setGifts(data.gifts || [])).catch(() => setGifts([]));
  }, []);

  const send = async (g) => {
    if (sending) return;
    setSending(true);
    try {
      const { data: res } = await api.post(`/messages/with/${userId}/gift`, { gift_id: g.id });
      setUser((u) => u && { ...u, coins: res.coins });
      toast.success(`🎁 ${g.name} sent!`);
      onSent(res.message);
      onClose();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="absolute bottom-[68px] left-0 right-0 z-30 px-3" data-testid="dm-gift-sheet">
      <div className="glass rounded-2xl p-3 border border-[#E6B84C]/30 max-h-56 overflow-y-auto">
        {gifts === null && <p className="text-zinc-500 text-xs text-center py-4">Loading gifts…</p>}
        <div className="grid grid-cols-4 gap-2">
          {gifts?.slice(0, 16).map((g) => (
            <button key={g.id} data-testid={`dm-gift-${g.id}`} onClick={() => send(g)}
              className="rounded-xl p-2 text-center bg-[#131A2A] border border-white/10 active:scale-95 transition-transform">
              {g.icon_url ? <img src={g.icon_url} alt="" className="w-8 h-8 object-contain mx-auto" /> : <p className="text-xl">{g.emoji}</p>}
              <p className="text-[8px] text-white font-bold truncate mt-0.5">{g.name}</p>
              <p className="text-[8px] text-[#E6B84C] font-bold flex items-center justify-center gap-0.5"><CoinIcon size={8} />{g.price}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function Thread({ userId }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { requestCall } = useCall();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [showGifts, setShowGifts] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const load = () => api.get(`/messages/with/${userId}`).then(({ data: d }) => alive && setData(d)).catch((e) => { toast.error(errText(e)); navigate("/messages"); });
    load();
    const t = setInterval(load, 4000);
    return () => { alive = false; clearInterval(t); };
  }, [userId, navigate]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [data?.messages?.length]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setText("");
    try {
      const { data: res } = await api.post(`/messages/with/${userId}`, { text: t });
      setData((d) => d && { ...d, messages: [...d.messages, res.message] });
    } catch (e) {
      toast.error(errText(e));
    }
  };

  if (!data) return <div className="min-h-screen max-w-md mx-auto flex items-center justify-center"><div className="w-8 h-8 rounded-full border-2 border-[#E6B84C] border-t-transparent animate-spin" /></div>;

  return (
    <div className="h-screen-dvh max-w-md mx-auto flex flex-col relative" data-testid="dm-thread">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-[#131A2A]/60 backdrop-blur-xl">
        <button data-testid="dm-back" onClick={() => navigate("/messages")} className="text-[#E6B84C]"><ArrowLeft size={18} /></button>
        <button onClick={() => navigate(`/profile/${data.user.id}`)} className="flex items-center gap-2.5 flex-1 min-w-0">
          <div className="relative">
            <Avatar src={data.user.avatar} size={34} />
            {data.user.online && <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-[#0B0F19]" />}
          </div>
          <div className="text-left min-w-0">
            <p className="text-white font-bold text-sm font-display truncate">{data.user.name}</p>
            <p className={`text-[9px] font-bold ${data.user.online ? "text-emerald-400" : "text-zinc-600"}`} data-testid="dm-online-status">{data.user.online ? "• Online" : "Offline"}</p>
          </div>
        </button>
        <button data-testid="dm-video-call" onClick={() => data.user.calls_enabled ? requestCall(data.user, "video") : toast("They aren't accepting calls right now")}
          className="text-[#E6B84C] active:scale-90 transition-transform"><Video size={19} /></button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2.5" data-testid="dm-messages">
        {data.messages.length === 0 && <p className="text-zinc-500 text-xs text-center mt-10">Say namaste 👋 — start the conversation!</p>}
        {data.messages.map((m) => (
          m.type === "gift" ? (
            <div key={m.id} className={`flex ${m.from_id === user?.id ? "justify-end" : "justify-start"}`} data-testid="dm-gift-bubble">
              <div className="max-w-[70%] rounded-2xl px-4 py-3 text-center border border-[#E6B84C]/40 bg-gradient-to-br from-[#8A2BE2]/20 to-[#FF007F]/10 shadow-[0_0_14px_rgba(230,184,76,0.15)]">
                {m.meta?.icon_url ? <img src={m.meta.icon_url} alt="" className="w-14 h-14 object-contain mx-auto" /> : <p className="text-3xl">{m.meta?.emoji || "🎁"}</p>}
                <p className="text-[#FDE08B] text-xs font-extrabold mt-1">{m.from_id === user?.id ? `You sent a ${m.meta?.gift_name}` : `Sent you a ${m.meta?.gift_name}!`}</p>
                <p className="text-[10px] text-zinc-400 font-bold flex items-center justify-center gap-1 mt-0.5"><CoinIcon size={10} /> {m.meta?.price?.toLocaleString("en-IN")} coins</p>
                <span className="block text-[8px] opacity-50 mt-1">{timeAgo(m.created_at)}</span>
              </div>
            </div>
          ) : (
          <div key={m.id} className={`flex ${m.from_id === user?.id ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[75%] px-3.5 py-2 text-sm rounded-2xl ${m.from_id === user?.id ? "gradient-primary text-white rounded-br-md" : "bg-[#131A2A] border border-white/10 text-zinc-100 rounded-bl-md"}`}>
              {m.text}
              <span className="block text-[8px] opacity-60 mt-0.5 text-right">{timeAgo(m.created_at)}</span>
            </div>
          </div>
          )
        ))}
        <div ref={endRef} />
      </div>
      {showGifts && <GiftSheet userId={userId} onClose={() => setShowGifts(false)} onSent={(msg) => setData((d) => d && { ...d, messages: [...d.messages, msg] })} />}
      <div className="px-3 py-3 pb-5 flex items-center gap-2 border-t border-white/10 bg-[#0B0F19]">
        <button data-testid="dm-gift-button" onClick={() => setShowGifts((s) => !s)}
          className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 active:scale-90 transition-transform ${showGifts ? "bg-[#E6B84C] text-[#0B0F19]" : "bg-[#131A2A] border border-[#E6B84C]/40 text-[#E6B84C]"}`}>
          <GiftIcon size={16} />
        </button>
        <input data-testid="dm-input" value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Type a message…"
          className="flex-1 bg-[#131A2A] border border-white/10 rounded-full px-4 py-2.5 text-sm text-white outline-none focus:border-[#E6B84C]/50 placeholder:text-zinc-500" />
        <button data-testid="dm-send" onClick={send}
          className="gradient-primary w-10 h-10 rounded-full flex items-center justify-center text-white active:scale-90 transition-transform">
          <Send size={16} />
        </button>
      </div>
    </div>
  );
}

export default function Messages() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [convs, setConvs] = useState(null);
  const [friends, setFriends] = useState([]);

  useEffect(() => {
    if (userId) return;
    let alive = true;
    const load = () => api.get("/messages/conversations").then(({ data }) => alive && setConvs(data.conversations)).catch(() => alive && setConvs([]));
    const loadFriends = () => api.get("/messages/online-friends").then(({ data }) => alive && setFriends(data.friends)).catch(() => {});
    load();
    loadFriends();
    const t = setInterval(load, 6000);
    const tf = setInterval(loadFriends, 30000);
    return () => { alive = false; clearInterval(t); clearInterval(tf); };
  }, [userId]);

  if (userId) return <Thread userId={userId} />;

  return (
    <div className="min-h-screen max-w-md mx-auto pb-28" data-testid="messages-page">
      <div className="flex items-center justify-between px-4 pt-6 pb-4">
        <div className="flex items-center gap-3">
          <button data-testid="messages-back" onClick={() => navigate(-1)} className="text-white/70"><ArrowLeft size={20} /></button>
          <h1 className="text-white font-bold text-[28px] leading-none">Messages</h1>
        </div>
        <button data-testid="messages-search" onClick={() => navigate("/search")} className="text-[#FF4D8D]"><Search size={20} /></button>
      </div>

      {friends.length > 0 && (
        <div className="flex gap-4 px-4 pb-4 overflow-x-auto no-scrollbar" data-testid="online-friends-row">
          {friends.map((f) => (
            <button key={f.id} data-testid={`online-friend-${f.id}`} onClick={() => navigate(`/messages/${f.id}`)}
              className="flex flex-col items-center shrink-0 w-16 active:scale-95 transition-transform">
              <div className="relative">
                <Avatar src={f.avatar} size={54} />
                <span className={`absolute bottom-0.5 right-0.5 w-3.5 h-3.5 rounded-full border-2 border-[#0B0F19] ${f.online ? "bg-emerald-400" : "bg-zinc-600"}`} />
              </div>
              <p className="text-white text-[10px] font-semibold mt-1 truncate w-full text-center">{f.name.split(" ")[0]}</p>
              <p className={`text-[8px] font-bold ${f.online ? "text-emerald-400" : "text-zinc-600"}`}>{f.online ? "Online" : "Offline"}</p>
            </button>
          ))}
        </div>
      )}

      <div className="px-4 space-y-1.5">
        {convs === null && Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton h-16 rounded-2xl" />)}
        {convs?.length === 0 && (
          <div className="text-center mt-16">
            <p className="text-4xl mb-3">💌</p>
            <p className="text-zinc-400 text-sm font-semibold">No messages yet</p>
            <p className="text-zinc-600 text-xs mt-1">Visit someone's profile and say hi!</p>
          </div>
        )}
        {convs && [...convs].sort((a, b) => (b.user.name?.includes("Yaara Team") ? 1 : 0) - (a.user.name?.includes("Yaara Team") ? 1 : 0)).map((c) => {
          const isTeam = c.user.name?.includes("Yaara Team");
          return (
            <button key={c.user.id} data-testid={`conversation-${c.user.id}`} onClick={() => navigate(`/messages/${c.user.id}`)}
              className={`w-full rounded-[20px] px-3.5 py-3 flex items-center gap-3 active:scale-[0.98] transition-transform ${isTeam ? "bg-[#1E1830] gold-hairline" : "bg-[#14101F] border border-white/[0.06]"}`}>
              <div className="relative shrink-0">
                {isTeam ? (
                  <span className="w-12 h-12 rounded-full gradient-primary flex items-center justify-center font-serif-display font-bold text-white text-xl shadow-[0_0_14px_rgba(255,46,151,0.35)]">Y</span>
                ) : (
                  <>
                    <div className="rounded-full p-[2px]" style={{ background: "conic-gradient(from 200deg, #FF2E97, #A03CF5, #FFD86B, #FF2E97)" }}>
                      <Avatar src={c.user.avatar} size={44} />
                    </div>
                    {friends.find((f) => f.id === c.user.id && f.online) && (
                      <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-emerald-400 border-2 border-[#0B0B14]" />
                    )}
                  </>
                )}
              </div>
              <div className="flex-1 text-left min-w-0">
                <p className="text-white font-bold text-sm truncate flex items-center gap-1">
                  {c.user.name}{isTeam ? <span className="text-[#FFD86B] text-[11px]">✦</span> : <span className="text-[#A03CF5] text-[11px]">✔</span>}
                </p>
                <p className={`text-xs truncate ${c.unread > 0 ? "text-white font-semibold" : "text-[#6E6683]"}`}>
                  {c.last_from_me ? "You: " : ""}{c.last_text}
                </p>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <span className="text-[10px] text-[#6E6683]">{timeAgo(c.last_at)}</span>
                {c.unread > 0 && <span className="bg-[#FF4D8D] text-white text-[9px] font-extrabold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center">{c.unread}</span>}
              </div>
            </button>
          );
        })}
      </div>
      <BottomNav />
    </div>
  );
}
